import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Bell, TrendingUp, TrendingDown } from 'lucide-react';
import { api } from '../../utils/api';

export function AlertModal({
  isOpen,
  onClose,
  metricConfig,
  selectedLocations = ['all'],
  availableLocations = [],
  locationNames = {},
  dateRange,
  selectedQuickDate,
  onSave,
  existingAlert = null,
  userSettings = null,
  currentOrganization = null
}) {
  const [alertConfig, setAlertConfig] = useState({
    metricType: metricConfig?.metricType || '',
    metricLabel: metricConfig?.metricLabel || '',
    condition: 'above', // 'above' or 'below'
    threshold: '',
    timeframe: selectedQuickDate || 'last7',
    locations: selectedLocations.includes('all') ? ['all'] : selectedLocations,
    alertName: '',
    notifyEmail: true,
    isActive: true
  });

  const [errors, setErrors] = useState({});

  // Store the initial alert ID to detect when we're editing a different alert
  const [initialAlertId, setInitialAlertId] = useState(null);

  // Track if modal was previously open to detect fresh opens
  const [wasPreviouslyOpen, setWasPreviouslyOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const currentAlertId = existingAlert?.id || null;
      const isFirstOpen = !wasPreviouslyOpen;

      // Only reinitialize if we're opening the modal fresh or editing a different alert
      if (isFirstOpen || initialAlertId !== currentAlertId) {
        setInitialAlertId(currentAlertId);
        setWasPreviouslyOpen(true);

        if (existingAlert) {
          // Editing existing alert
          setAlertConfig({
            metricType: existingAlert.metricType,
            metricLabel: existingAlert.metricLabel || existingAlert.metricType,
            condition: existingAlert.condition,
            threshold: existingAlert.threshold,
            timeframe: existingAlert.timeframe,
            locations: existingAlert.locations || ['all'],
            alertName: existingAlert.alertName,
            notifyEmail: existingAlert.notifyEmail !== false,
            isActive: existingAlert.isActive !== false
          });
        } else if (metricConfig) {
          // Creating new alert
          setAlertConfig(prev => ({
            ...prev,
            metricType: metricConfig.metricType,
            metricLabel: metricConfig.metricLabel,
            timeframe: selectedQuickDate || 'last7',
            locations: selectedLocations.includes('all') ? ['all'] : selectedLocations,
            alertName: `${metricConfig.metricLabel} Alert`
          }));
        }
      }
    } else {
      // Reset when modal closes
      setInitialAlertId(null);
      setWasPreviouslyOpen(false);
    }
  }, [isOpen, metricConfig, selectedQuickDate, selectedLocations]);

  const validateForm = () => {
    const newErrors = {};

    if (!alertConfig.alertName.trim()) {
      newErrors.alertName = 'Alert name is required';
    }

    if (!alertConfig.metricType || !alertConfig.metricType.trim()) {
      newErrors.metricType = 'Metric type is required. Please create alerts from the Dashboard.';
    }

    if (!alertConfig.threshold || isNaN(alertConfig.threshold) || parseFloat(alertConfig.threshold) <= 0) {
      newErrors.threshold = 'Please enter a valid threshold value';
    }

    if (alertConfig.locations.length === 0) {
      newErrors.locations = 'Please select at least one location';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      if (existingAlert) {
        // Editing existing alert - need to call API directly using PATCH
        try {
          await api.patch(`/alerts`, {
            id: existingAlert.id,
            userId: existingAlert.userId,
            alertName: alertConfig.alertName,
            condition: alertConfig.condition,
            threshold: alertConfig.threshold,
            timeframe: alertConfig.timeframe,
            locations: alertConfig.locations,
            notifyEmail: alertConfig.notifyEmail,
            isActive: alertConfig.isActive
          });
          onSave && onSave(alertConfig);
          handleClose();
        } catch (error) {
          console.error('Error updating alert:', error);
          alert('Failed to update alert. Please try again.');
        }
      } else {
        // Creating new alert - let parent handle it
        onSave(alertConfig);
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setAlertConfig({
      metricType: '',
      metricLabel: '',
      condition: 'above',
      threshold: '',
      timeframe: 'last7',
      locations: ['all'],
      alertName: '',
      notifyEmail: true,
      isActive: true
    });
    setErrors({});
    onClose();
  };

  const handleLocationToggle = (locationId) => {
    setAlertConfig(prev => {
      if (locationId === 'all') {
        return { ...prev, locations: ['all'] };
      }

      const locationIdStr = locationId.toString();
      const currentSelection = prev.locations.filter(id => id !== 'all');

      let newLocations;
      if (currentSelection.includes(locationIdStr)) {
        newLocations = currentSelection.filter(id => id !== locationIdStr);
      } else {
        newLocations = [...currentSelection, locationIdStr];
        if (newLocations.length === availableLocations.length) {
          return { ...prev, locations: ['all'] };
        }
      }

      return { ...prev, locations: newLocations };
    });
  };

  const getLocationDisplayName = (locationId) => {
    const isDemoOrg = localStorage.getItem('currentOrgCode') === 'DEMO';
    if (isDemoOrg) {
      return `Location ${locationId}`;
    }
    return locationNames[parseInt(locationId)] || `Location ${locationId}`;
  };

  const getSelectedLocationText = () => {
    if (alertConfig.locations.includes('all')) {
      return 'All Locations';
    }
    if (alertConfig.locations.length === 0) {
      return 'Select Locations';
    }
    if (alertConfig.locations.length === 1) {
      return getLocationDisplayName(alertConfig.locations[0]);
    }
    return `${alertConfig.locations.length} Locations`;
  };

  const timeframeOptions = [
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 days' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'last60', label: 'Last 60 days' },
    { value: 'last90', label: 'Last 90 days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' }
  ];

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'var(--lodge-white)',
          borderRadius: 16,
          maxWidth: 600,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: 'var(--shadow-xl)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 24,
            borderBottom: '1px solid var(--lodge-onyx-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                backgroundColor: 'var(--lodge-orange-400)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Bell size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>
                {existingAlert ? 'Edit Alert' : 'Create Alert'}
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--lodge-onyx-400)' }}>
                {metricConfig?.metricLabel || alertConfig.metricLabel}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              color: 'var(--lodge-onyx-400)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {/* Error Message for Missing Metric Type */}
          {errors.metricType && (
            <div
              style={{
                padding: 12,
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                marginBottom: 20
              }}
            >
              <p style={{ margin: 0, fontSize: 14, color: '#dc2626' }}>
                {errors.metricType}
              </p>
            </div>
          )}

          {/* Alert Name */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--lodge-onyx-900)',
                marginBottom: 8
              }}
            >
              Alert Name
            </label>
            <input
              type="text"
              value={alertConfig.alertName}
              onChange={(e) => setAlertConfig(prev => ({ ...prev, alertName: e.target.value }))}
              placeholder="Enter a name for this alert"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${errors.alertName ? '#ef4444' : 'var(--lodge-onyx-100)'}`,
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'inherit',
                outline: 'none'
              }}
            />
            {errors.alertName && (
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ef4444' }}>
                {errors.alertName}
              </p>
            )}
          </div>

          {/* Condition and Threshold */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--lodge-onyx-900)',
                marginBottom: 8
              }}
            >
              Alert Condition
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <select
                value={alertConfig.condition}
                onChange={(e) => setAlertConfig(prev => ({ ...prev, condition: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid var(--lodge-onyx-100)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  outline: 'none',
                  backgroundColor: 'var(--lodge-white)',
                  cursor: 'pointer'
                }}
              >
                <option value="above">Above</option>
                <option value="below">Below</option>
              </select>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="number"
                  step="0.01"
                  value={alertConfig.threshold}
                  onChange={(e) => setAlertConfig(prev => ({ ...prev, threshold: e.target.value }))}
                  placeholder="Threshold"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${errors.threshold ? '#ef4444' : 'var(--lodge-onyx-100)'}`,
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    outline: 'none'
                  }}
                />
                {metricConfig?.unit && (
                  <span
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 14,
                      color: 'var(--lodge-onyx-400)',
                      pointerEvents: 'none'
                    }}
                  >
                    {metricConfig.unit}
                  </span>
                )}
              </div>
            </div>
            {errors.threshold && (
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ef4444' }}>
                {errors.threshold}
              </p>
            )}
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--lodge-onyx-400)' }}>
              Alert will trigger when {metricConfig?.metricLabel} is {alertConfig.condition} {alertConfig.threshold || '___'}
              {metricConfig?.unit}
            </p>
          </div>

          {/* Timeframe */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--lodge-onyx-900)',
                marginBottom: 8
              }}
            >
              Timeframe
            </label>
            <select
              value={alertConfig.timeframe}
              onChange={(e) => setAlertConfig(prev => ({ ...prev, timeframe: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--lodge-onyx-100)',
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'inherit',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {timeframeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--lodge-onyx-400)' }}>
              Alert will evaluate data for the selected timeframe on a rolling basis
            </p>
          </div>

          {/* Location Filter */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--lodge-onyx-900)',
                marginBottom: 8
              }}
            >
              Locations
            </label>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  const dropdown = document.getElementById('alert-location-dropdown');
                  dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${errors.locations ? '#ef4444' : 'var(--lodge-onyx-100)'}`,
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  backgroundColor: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{getSelectedLocationText()}</span>
                <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>▼</span>
              </button>

              <div
                id="alert-location-dropdown"
                style={{
                  display: 'none',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  backgroundColor: 'var(--lodge-white)',
                  border: '1px solid var(--lodge-onyx-100)',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  maxHeight: 200,
                  overflow: 'auto',
                  zIndex: 10
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--lodge-onyx-100)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={alertConfig.locations.includes('all')}
                    onChange={() => handleLocationToggle('all')}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ fontSize: 14, color: '#1e293b' }}>All Locations</span>
                </label>
                {availableLocations.map(locationId => (
                  <label
                    key={locationId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={alertConfig.locations.includes(locationId.toString())}
                      onChange={() => handleLocationToggle(locationId)}
                      style={{ marginRight: 8 }}
                    />
                    <span style={{ fontSize: 14, color: '#1e293b' }}>
                      {getLocationDisplayName(locationId)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {errors.locations && (
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ef4444' }}>
                {errors.locations}
              </p>
            )}
          </div>

          {/* Notification Settings */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 12,
                backgroundColor: 'var(--lodge-onyx-50)',
                borderRadius: 8,
                border: '1px solid #e2e8f0'
              }}
            >
              <input
                type="checkbox"
                checked={alertConfig.notifyEmail}
                onChange={(e) => setAlertConfig(prev => ({ ...prev, notifyEmail: e.target.checked }))}
                style={{ marginRight: 12 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>
                  Email Notifications
                </div>
                <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 2 }}>
                  Receive email alerts when conditions are met
                </div>
              </div>
            </label>
          </div>

          {/* Data Completeness Info */}
          <div
            style={{
              padding: 16,
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              marginBottom: 20
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>
                  Data Completeness Requirement
                </div>
                <div style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                  Alerts are evaluated daily but only sent when <strong>all selected locations</strong> have reported data for the prior day. This ensures accurate calculations before triggering notifications.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 24,
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              border: '1px solid var(--lodge-onyx-100)',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--lodge-onyx-400)',
              backgroundColor: 'var(--lodge-white)',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              color: 'white',
              backgroundColor: '#f97316',
              cursor: 'pointer'
            }}
          >
            {existingAlert ? 'Update Alert' : 'Create Alert'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
